import React, { useState } from "react";
import { Mail, ChevronRight, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { VerifyEmailSheet } from "@/components/VerifyEmailSheet";

const DISMISS_KEY = "kinly.verify_banner_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24; // 24h snooze

/**
 * UnverifiedBanner — shown on Today + You when user.email_verified is false.
 * Dismiss snoozes for 24h. Tap opens the VerifyEmailSheet.
 */
export function UnverifiedBanner() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const v = localStorage.getItem(DISMISS_KEY);
      if (!v) return false;
      return Date.now() - parseInt(v, 10) < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  });

  if (!user || user.email_verified || dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <>
      <div
        data-testid="unverified-banner"
        className="w-full rounded-2xl p-4 border border-primary/30 bg-primary/[0.05] flex items-start gap-3 text-left"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex-1 flex items-start gap-3 text-left tap"
          aria-label="Verify your email"
        >
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Mail className="w-3.5 h-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-primary font-medium">
              One last step
            </p>
            <p className="text-[14px] font-medium mt-0.5">
              Verify your email to invite people.
            </p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              We sent a 6-digit code to {user.email}.
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground self-center" />
        </button>
        <button
          type="button"
          data-testid="unverified-banner-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="w-7 h-7 -mr-1 -mt-1 inline-flex items-center justify-center text-muted-foreground hover:text-foreground tap shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <VerifyEmailSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}
